import { auth } from '../firebase';
import { API_BASE_URL } from '../config';
import { writeAuditLogSafe } from './auditLogService';
import { notifyError, notifySuccess } from '../notify';

const normalizeRole = (role) => {
  const r = String(role || '').trim().toLowerCase();
  if (r === 'sysadmin' || r === 'system_admin' || r === 'system admin') return 'sysadmin';
  if (r === 'admin') return 'admin';
  if (r === 'user') return 'user';
  return '';
};

const getStoredUser = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || '{}');
  } catch {
    return {};
  }
};

export const validateStep1 = ({ name, username, email, password }) => {
  const trimmedName = String(name || '').trim();
  const trimmedUsername = String(username || '').trim();
  const trimmedEmail = String(email || '').trim();

  if (!trimmedName) return { ok: false, message: 'Name is required.' };
  if (!trimmedUsername) return { ok: false, message: 'Username is required.' };
  if (!trimmedEmail) return { ok: false, message: 'Email is required.' };
  if (!password) return { ok: false, message: 'Password is required.' };
  if (String(password).length < 6) {
    return { ok: false, message: 'Password must be at least 6 characters.' };
  }
  return {
    ok: true,
    normalized: {
      name: trimmedName,
      username: trimmedUsername,
      email: trimmedEmail,
      password: String(password),
    },
  };
};

export const validatePasswordsMatch = ({ password, confirmPassword }) => {
  if (!password) return { ok: false, message: 'Password is required.' };
  if (String(password).length < 6) {
    return { ok: false, message: 'Password must be at least 6 characters.' };
  }
  if (!confirmPassword) return { ok: false, message: 'Confirm password is required.' };
  if (String(password) !== String(confirmPassword)) {
    return { ok: false, message: 'Passwords do not match.' };
  }
  return { ok: true };
};

export const validateLocation = ({ region, province, municipality, barangay }) => {
  const r = String(region || '').trim();
  const p = String(province || '').trim();
  const m = String(municipality || '').trim();
  const b = String(barangay || '').trim();

  if (!r) return { ok: false, message: 'Region is required.' };
  if (!p) return { ok: false, message: 'Province is required.' };
  if (!m) return { ok: false, message: 'Municipality is required.' };
  if (!b) return { ok: false, message: 'Barangay is required.' };

  return {
    ok: true,
    normalized: { region: r, province: p, municipality: m, barangay: b },
  };
};

export const provisionUser = async ({
  name,
  username,
  email,
  password,
  role, // 'user' | 'admin' | 'sysadmin'
  adminaccessLevel,
  location,
}) => {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) {
    throw new Error('Invalid role');
  }

  // Creating a user with Auth `emailVerified=true` requires Admin SDK, so we do provisioning server-side.
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error('Not signed in. Please sign in again and retry.');
  }

  const idToken = await currentUser.getIdToken(true);
  const storedUser = getStoredUser();

  const actor = {
    uid: currentUser.uid || null,
    email: currentUser.email || 'unknown',
    role: storedUser?.role || 'unknown',
    name: storedUser?.name || null,
    username: storedUser?.username || null,
    isAuthenticated: true,
  };

  const resp = await fetch(`${API_BASE_URL}/api/users/provision`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${idToken}`,
    },
    body: JSON.stringify({
      name,
      username,
      email,
      password,
      role: normalizedRole,
      adminaccessLevel: adminaccessLevel || 'none',
      location: {
        region: location?.region || null,
        province: location?.province || null,
        municipality: location?.municipality || null,
        barangay: location?.barangay || null,
      },
    }),
  });

  const payload = await resp.json().catch(() => ({}));
  if (!resp.ok || payload?.ok !== true) {
    const message = payload?.error || 'Failed to create user.';

    // Show in-web notification for failure
    if (payload?.notificationId) {
      notifyError(`${message} (notification sent)`);
    } else {
      notifyError(message);
    }

    await writeAuditLogSafe({
      action: 'User provisioning failed',
      details: message,
      severity: 'Warning',
      category: 'user_management',
      outcome: 'failure',
      actor,
      resource: {
        type: 'user',
        id: null,
        name: email || null,
        path: '/api/users/provision',
      },
      metadata: {
        role: normalizedRole,
        hasLocation: !!location,
      },
      tags: ['user-management', 'provision', 'failed'],
    });

    const err = new Error(message);
    err.code = payload?.code || 'PROVISION_FAILED';
    throw err;
  }

  notifySuccess(`Created ${normalizedRole} account for ${email}.`);

  await writeAuditLogSafe({
    action: 'User provisioned',
    details: `Created ${normalizedRole} account for ${email}.`,
    severity: 'Info',
    category: 'user_management',
    outcome: 'success',
    actor,
    resource: {
      type: 'user',
      id: payload.uid || null,
      name: email || null,
      path: '/api/users/provision',
    },
    metadata: {
      targetRole: normalizedRole,
      hasLocation: !!location,
      location: {
        region: location?.region || null,
        province: location?.province || null,
        municipality: location?.municipality || null,
        barangay: location?.barangay || null,
      },
    },
    tags: ['user-management', 'provision', 'success'],
  });

  return { uid: payload.uid, sentEmail: payload.sentEmail === true };
};